// OrgChartTree.tsx
import React, { useEffect, useState } from 'react';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useEmployeeService, EmployeeType } from '../services/employeeService';

interface OrgChartTreeProps {
  onSelect: (employee: EmployeeType & { selectTree?: string[]; deselectTree?: string[] }) => void;
  selectedUsers: string[];
  onStep?: (manager: EmployeeType, depth: number) => void;
}

interface TreeState {
  [email: string]: {
    reports: EmployeeType[] | null;
    loading: boolean;
    error: string | null;
  };
}

const findManagerByEmail = (
  email: string,
  topManagers: EmployeeType[],
  treeState: TreeState
): EmployeeType | undefined => {
  let found = topManagers.find(m => m.employee_email === email);
  if (found) return found;
  for (const state of Object.values(treeState)) {
    if (state.reports) {
      found = state.reports.find(m => m.employee_email === email);
      if (found) return found;
    }
  }
  return undefined;
};

const OrgChartTree: React.FC<OrgChartTreeProps> = ({ onSelect, selectedUsers, onStep }) => {
  const employeeService = useEmployeeService();
  const [topManagers, setTopManagers] = useState<EmployeeType[]>([]);
  const [treeState, setTreeState] = useState<TreeState>({});
  const [expanded, setExpanded] = useState<string[]>([]);
  const [selectLoading, setSelectLoading] = useState<string | null>(null);

  useEffect(() => {
    employeeService.fetchManagers().then(setTopManagers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    expanded.forEach(email => maybeLoadManager(email));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, treeState, topManagers]);

  function maybeLoadManager(email: string) {
    if (!treeState[email]) {
      const manager = findManagerByEmail(email, topManagers, treeState);
      if (!manager) return;
      setTreeState(prev => ({
        ...prev,
        [email]: { reports: null, loading: true, error: null }
      }));
      employeeService
        .fetchSubordinateManagers(manager.employee_email, manager.manager_level)
        .then(subManagers =>
          setTreeState(prev => ({
            ...prev,
            [email]: { reports: subManagers, loading: false, error: null }
          }))
        )
        .catch(err =>
          setTreeState(prev => ({
            ...prev,
            [email]: { reports: [], loading: false, error: err.message || 'Failed to load reports' }
          }))
        );
      if (onStep) {
        let depth = 1;
        let m = manager;
        while (m.manager_level > 1) {
          depth++;
          m = findManagerByEmail(
            m[`manager_level_${m.manager_level - 1}_email`],
            topManagers,
            treeState
          ) || m;
          if (!m) break;
        }
        onStep(manager, depth);
      }
    }
  }

  // Recursive fetch all reports under a manager
  const getAllReportEmails = async (manager: EmployeeType): Promise<string[]> => {
    try {
      const allReports = await employeeService.fetchAllReports(manager.employee_email);
      return [manager.employee_email, ...allReports.map(e => e.employee_email)];
    } catch {
      return [manager.employee_email];
    }
  };

  // Enhanced handleCheck for recursive select/deselect
  const handleCheck = async (employee: EmployeeType) => {
    setSelectLoading(employee.employee_email);
    const isSelected = selectedUsers.includes(employee.employee_email);
    const allEmails = await getAllReportEmails(employee);
    setSelectLoading(null);
    onSelect({
      ...employee,
      selectTree: isSelected ? undefined : allEmails,
      deselectTree: isSelected ? allEmails : undefined
    });
  };

  const handleExpandedItemsChange = (
    _event: React.SyntheticEvent | null,
    itemIds: string[]
  ) => {
    setExpanded(itemIds);
  };

  const renderTreeChildren = (node: EmployeeType, depth: number) => {
    const state = treeState[node.employee_email];
    if (state?.loading) {
      return <Box sx={{ pl: 3 }}><CircularProgress size={18} /></Box>;
    }
    if (state?.error) {
      return <Box sx={{ pl: 3, color: 'error.main' }}>{state.error}</Box>;
    }
    if (state?.reports && state.reports.length > 0) {
      return state.reports.map(report => renderTree(report, depth + 1));
    }
    if (state?.reports?.length === 0) {
      return <Box sx={{ pl: 3, color: 'text.secondary' }}>No reports</Box>;
    }
    // Show nothing if not loaded but expandable
    if (node.manager_level < 8 && node.id) {
      return <Box sx={{ display: 'none' }} />;
    }
    return null;
  };

  const renderTree = (node: EmployeeType, depth: number = 0) => {
    const isLoading = selectLoading === node.employee_email;

    return (
      <TreeItem
        key={node.employee_email}
        itemId={node.employee_email}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', ml: depth * 2 }}>
            <Checkbox
              checked={selectedUsers.includes(node.employee_email)}
              onChange={async () => await handleCheck(node)}
              size="small"
              disabled={isLoading}
            />
            <Typography variant="body2" component="span" sx={{ ml: 1 }}>
              {node.employee_email}
            </Typography>
            {isLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Box>
        }
      >
        {renderTreeChildren(node, depth)}
      </TreeItem>
    );
  };

  return (
    <SimpleTreeView
      expandedItems={expanded}
      onExpandedItemsChange={handleExpandedItemsChange}
      slots={{
        expandIcon: ChevronRightIcon,
        collapseIcon: ExpandMoreIcon,
      }}
      expansionTrigger="iconContainer"
      sx={{ maxHeight: 400, overflowY: 'auto', p: 1, borderRadius: 2 }}
    >
      {topManagers.map(manager => renderTree(manager, 0))}
    </SimpleTreeView>
  );
};

export default OrgChartTree;